import { getConfig } from '@/config/chatConfig';
import { AvailableLanguage, ChatWidgetConfig } from '@/types';
import { deepMerge } from '@/utils/deepMerge';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useChatStore } from './chatStore';

const LANGUAGE_STORAGE_KEY = 'chatWidget1648Language';

const isAvailableLanguage = (value: string | null): value is AvailableLanguage => {
  return value === 'en' || value === 'de';
};

const getStoredLanguage = (): AvailableLanguage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = window.sessionStorage.getItem(LANGUAGE_STORAGE_KEY);

  return isAvailableLanguage(stored) ? stored : null;
};

export type ConfigState = {
  config: ChatWidgetConfig;
  baseConfig: Partial<ChatWidgetConfig> | null;
  setConfig: (config: ChatWidgetConfig) => void;
  updateConfig: (config: Partial<ChatWidgetConfig>) => void;
  setLanguage: (language: AvailableLanguage) => void;
  setContextSuggestions: (
    contextSuggestions: Record<
      string,
      { suggestion: string; chatQuestion?: string; quickAnswer?: string; description?: string }
    >
  ) => void;
};

export const useConfigStore = create<ConfigState>()(
  devtools((set, get) => {
    const initialLanguage = getStoredLanguage() ?? 'de';

    return {
      config: deepMerge(getConfig(initialLanguage), { language: initialLanguage }),
      baseConfig: null,
      setConfig: (config: ChatWidgetConfig) => {
        const storedLanguage = getStoredLanguage();
        const language = storedLanguage ?? config.language ?? 'de';
        const baseConfig = { ...config, language };

        set(
          {
            config: deepMerge(getConfig(language), baseConfig),
            baseConfig,
          },
          false,
          'setConfig'
        );

        if (!storedLanguage && typeof window !== 'undefined') {
          window.sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language);
        }

        if (config.ui?.fullScreenModeByDefault ?? false) {
          useChatStore.getState().setFullScreen(true);
        }
      },
      updateConfig: (configOverrides: Partial<ChatWidgetConfig>) => {
        const storedLanguage = getStoredLanguage();
        const requestedLanguage = configOverrides.language;
        const language = requestedLanguage ?? storedLanguage ?? get().config.language ?? 'de';
        const baseConfig = deepMerge(get().baseConfig ?? {}, { ...configOverrides, language });

        set(
          {
            config: deepMerge(getConfig(language), baseConfig),
            baseConfig,
          },
          false,
          'updateConfig'
        );

        if (typeof window !== 'undefined' && requestedLanguage) {
          window.sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language);
        }

        if (configOverrides.ui?.fullScreenModeByDefault ?? false) {
          useChatStore.getState().setFullScreen(true);
        }

        if (configOverrides.webhookUrl) {
          useChatStore.getState().setWebhookUrl(configOverrides.webhookUrl);
        }
      },
      setLanguage: (language: AvailableLanguage) => {
        const currentLanguage = get().config.language ?? 'de';

        if (currentLanguage === language) {
          return;
        }

        const baseConfig = get().baseConfig ?? {};
        const configOverrides = { ...baseConfig, language };

        set(
          {
            config: deepMerge(getConfig(language), configOverrides),
            baseConfig: configOverrides,
          },
          false,
          'setLanguage'
        );

        useChatStore.getState().resetSession();

        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(LANGUAGE_STORAGE_KEY, language);
        }
      },
      setContextSuggestions: (
        contextSuggestions: Record<
          string,
          { suggestion: string; chatQuestion?: string; quickAnswer?: string; description?: string }
        >
      ) => {
        set({
          config: { ...get().config, contextSuggestions },
        });
      },
    };
  })
);
